import styled from '@emotion/styled';
import { getKeyDisplayname } from '@shared/constants/Keybindings';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useGlobalCustomControlsStore } from '@shared/hooks/state/GlobalCustomControlsContainer';
import { MacroDisplayEvent, MacroEventType } from '@shared/models/deviceProfiles/MacroEvent';
import { Trans, useTranslation } from 'react-i18next';
export interface IMacroEventDescriptionProps {
    event: MacroDisplayEvent;
}

const getJoystickMoveLabel = (event: MacroDisplayEvent, isLeft: boolean) => {
    let label = '';
    const xValueToCheck = (isLeft ? event.joystickLeftMoveX : event.joystickRightMoveX) ?? 0;
    const yValueToCheck = (isLeft ? event.joystickLeftMoveY : event.joystickRightMoveY) ?? 0;
    // if we have X and Y as non zero add both details to the label
    if (xValueToCheck != 0 && yValueToCheck != 0) {
        label += `${Math.abs(xValueToCheck)}% ${xValueToCheck < 0 ? 'left' : 'right'} and ${Math.abs(yValueToCheck)}% ${
            yValueToCheck > 0 ? 'up' : 'down'
        }`;
    }
    // if we have only X or Y as non zero add only that detail to the label
    else if (xValueToCheck != 0) {
        label += `${Math.abs(xValueToCheck)}% ${xValueToCheck < 0 ? 'left' : 'right'}`;
    } else if (yValueToCheck != 0) {
        label += `${Math.abs(yValueToCheck)}% ${yValueToCheck > 0 ? 'up' : 'down'}`;
    }

    return label;
};

const getMouseMoveLabel = (event: MacroDisplayEvent) => {
    let label = '';
    let mouseEvents = [];

    if (event.mouseMoveX != 0 && event.mouseMoveX !== undefined) {
        const directionX = event.mouseMoveX >= 0 ? 'right' : 'left';
        mouseEvents.push(`${Math.abs(event.mouseMoveX) ?? 0}px ${directionX}`);
    }

    if (event.mouseMoveY != 0 && event.mouseMoveY !== undefined) {
        const directionY = event.mouseMoveY <= 0 ? 'up' : 'down';
        mouseEvents.push(`${Math.abs(event.mouseMoveY) ?? 0}px ${directionY}`);
    }
    label = mouseEvents.join(' and ');

    return label;
};

const checkJoystick = (target: any) => target !== 0 && target !== undefined && target !== null;

function MacroEventDescription({ event }: IMacroEventDescriptionProps) {
    const { t } = useTranslation();

    const defaultLocale = useEditProfileStore((state) => state.defaultLocale);
    const locale = defaultLocale;

    const getDeviceControlDisplayName = (controlId: string) => {
        const control = useGlobalCustomControlsStore.getState().getDeviceControlName(controlId);
        return control;
    };

    const getKeyPressLabel = (event: MacroDisplayEvent) => {
        const keys = event.keys?.map((k) => getKeyDisplayname(k, locale))?.join(' · ');

        if (event.holdTimeMilliseconds && event.holdTimeMilliseconds > 0) {
            return (
                <Trans>
                    {t('common:event.keyPress.pressLabel', 'Press keys', {
                        value: keys,
                        time: event.holdTimeMilliseconds,
                    })}
                </Trans>
            );
        } else if (event.isKeyLatch) {
            return (
                <Trans>
                    {t('common:event.keyPress.latchLabel', 'Latch keys', {
                        value: keys,
                    })}
                </Trans>
            );
        }

        return (
            <Trans>
                {t('common:event.keyPress.tapLabel', 'Tap keys', {
                    value: keys,
                })}
            </Trans>
        );
    };
    const eventLabels: Record<MacroEventType, (event: MacroDisplayEvent) => React.ReactNode> = {
        KeyPress: (event) => {
            return <>{getKeyPressLabel(event)}</>;
        },
        KeyRelease: (event) => (
            <Trans>
                {t('common:event.keyRelease.label', 'Release', {
                    value: event.keys?.map((k) => getKeyDisplayname(k, locale))?.join(' · '),
                })}
            </Trans>
        ),
        KeyToggle: (event) => (
            <Trans>
                {t('common:event.keyToggle.label', 'Toggle', {
                    value: event.keys?.map((k) => getKeyDisplayname(k, locale))?.join(' · '),
                })}
            </Trans>
        ),
        Type: (event) => (
            <Trans>
                {t('common:event.type.label', 'Type', {
                    value: event.typedPhrase,
                })}
            </Trans>
        ),
        PlayAudio: (event) => (
            <Trans>
                {t('common:event.playAudio.label', 'Speak', {
                    value: event.outputSpeech,
                })}
            </Trans>
        ),
        JoysticksMove: (event) => {
            return (
                <>
                    <p>
                        {(checkJoystick(event.joystickLeftMoveX) || checkJoystick(event.joystickLeftMoveY)) && (
                            <span>{t('common:event.joystickMove.labelLeft')}</span>
                        )}
                        <b>{getJoystickMoveLabel(event, true)}</b>
                    </p>
                    <p>
                        {(checkJoystick(event.joystickRightMoveX) || checkJoystick(event.joystickRightMoveY)) && (
                            <span>{t('common:event.joystickMove.labelRight')}</span>
                        )}
                        <b>{getJoystickMoveLabel(event, false)}</b>
                        {event.holdTimeMilliseconds && event.holdTimeMilliseconds > 0
                            ? ` for ${event.holdTimeMilliseconds} milliseconds`
                            : null}
                    </p>
                </>
            );
        },
        MouseMove: (event) => (
            <Trans>
                {t('common:event.mouseMove.label', 'Move mouse', {
                    value: getMouseMoveLabel(event),
                })}
            </Trans>
        ),
        Pause: (event) => (
            <Trans>
                {t('common:event.pause.label', 'Pause', {
                    value: event.holdTimeMilliseconds,
                })}
            </Trans>
        ),
        StopOutputs: () => <>{t('common:event.stopOutputs.label')}</>,
        DeviceTypeCustomAction: (event) => (
            <>{event.deviceTypeCustomActionId && getDeviceControlDisplayName(event.deviceTypeCustomActionId)}</>
        ),
    };
    const labelGenerator = eventLabels[event.eventType as MacroEventType];

    return <TextWrap>{labelGenerator ? labelGenerator(event) : ''}</TextWrap>;
}

const TextWrap = styled.div`
    word-break: break-all; // forces breaks within words if needed
`;

export default MacroEventDescription;
